import posthog from 'posthog-js';
import type {PostHogConfig} from 'posthog-js';

const DEFAULT_HOST = 'https://us.i.posthog.com';

/** Flip to true to send from Vite DEV. Leave false in committed code. */
const ENABLE_ANALYTICS_IN_DEV = false;
/** Paste `phc_…` here for local Vite. Empty in committed code. Deployed builds use `VITE_POSTHOG_KEY`. */
const LOCAL_POSTHOG_KEY = '';
/** Deploy envs (VITE_DEPLOY_ENV) allowed to report to PostHog. staging/vbatest/dev stay dark. */
const ANALYTICS_ENVS = new Set(['prod', 'production']);

type ViteAnalyticsEnv = {
  VITE_POSTHOG_KEY?: string;
  VITE_POSTHOG_HOST?: string;
  VITE_DEPLOY_ENV?: string;
  DEV: boolean;
};

function analyticsEnv(): ViteAnalyticsEnv {
  return (import.meta as unknown as {env: ViteAnalyticsEnv}).env;
}

/**
 * Reveals an element's subtree in session replay on apps with `revealUnmaskClass` on. posthog-js has
 * no built-in unmask class; `REVEAL_MASK_TEXT_SELECTOR` gives this one its meaning. Only put it on
 * subtrees with no PII.
 */
export const POSTHOG_UNMASK_CLASS = 'ph-no-mask';

// Replay masks text whose element or any ancestor matches, so wrappers around a revealed subtree
// are excluded too (`:has`). Loose text directly inside such a wrapper is therefore not masked.
const REVEAL_MASK_TEXT_SELECTOR = `*:not(.${POSTHOG_UNMASK_CLASS}):not(.${POSTHOG_UNMASK_CLASS} *):not(:has(.${POSTHOG_UNMASK_CLASS}))`;

// Replay treats a selector error as "don't mask", so without `:has` support keep the project setting.
function supportsHasSelector(): boolean {
  return typeof CSS !== 'undefined' && CSS.supports('selector(:has(*))');
}

export type AnalyticsProperty = string | number | boolean | null;
export type AnalyticsProperties = Record<string, AnalyticsProperty>;

export type AnalyticsCapture = {
  /**
   * Session replay. Default true.
   * Records DOM snapshots so you can watch a visit. Input values are NOT
   * included unless maskAllInputs is false. PII rendered as plain text
   * (not in an `<input>`) is still visible — add class `ph-no-capture` on
   * those elements to replace them with a blank block in replay.
   */
  sessionRecording?: boolean;
  /**
   * Mask every `<input>`, `<textarea>`, and `<select>` in replay with asterisks.
   * Default true. Runs in the browser; raw values never leave the device.
   * Only turn off if the app has no sensitive fields.
   */
  maskAllInputs?: boolean;
  /**
   * Mask all replay text except `POSTHOG_UNMASK_CLASS` subtrees. Default false (text masking follows
   * the PostHog project setting). Overrides the project's text masking for this app.
   */
  revealUnmaskClass?: boolean;
  /**
   * Store request/response bodies from network recordings. Default false.
   * Network tab still shows URL, method, status, and timing.
   * Dangerous on apps that POST PII (enrollment submit includes SSN).
   */
  recordNetworkBodies?: boolean;
  /**
   * Click/change autocapture (separate from replay). Default false.
   * Can attach input values to click events. Prefer explicit `track()` calls.
   */
  autocapture?: boolean;
  /**
   * Automatic `$pageview` on SPA navigations. Default true.
   */
  capturePageviews?: boolean;
  /**
   * Person profiles in PostHog. Default `'identified_only'`.
   * `'never'` = anonymous events only (enrollment).
   * `'identified_only'` = profile created after `identify()`.
   * `'always'` = profile for every visitor (usually skip this).
   */
  personProfiles?: 'never' | 'identified_only' | 'always';
};

export const DEFAULT_ANALYTICS_CAPTURE: Required<AnalyticsCapture> = {
  sessionRecording: true,
  maskAllInputs: true,
  revealUnmaskClass: false,
  recordNetworkBodies: false,
  autocapture: false,
  capturePageviews: true,
  personProfiles: 'identified_only',
};

export function mergeAnalyticsCapture(overrides?: AnalyticsCapture): Required<AnalyticsCapture> {
  return {...DEFAULT_ANALYTICS_CAPTURE, ...overrides};
}

export function analyticsDeployEnv(): string {
  const env = analyticsEnv();
  if (env.VITE_DEPLOY_ENV) return env.VITE_DEPLOY_ENV;
  return env.DEV ? 'development' : 'production';
}

function shouldInit(): {key: string; host: string} | null {
  const env = analyticsEnv();
  if (env.DEV && !ENABLE_ANALYTICS_IN_DEV) return null;
  // Fail closed: a deployed build must explicitly declare an allowlisted
  // VITE_DEPLOY_ENV. A missing value stays dark rather than defaulting to prod.
  if (!env.DEV && !ANALYTICS_ENVS.has(env.VITE_DEPLOY_ENV ?? '')) return null;
  const key = env.VITE_POSTHOG_KEY?.trim() || (env.DEV ? LOCAL_POSTHOG_KEY.trim() : '');
  if (!key) return null;
  return {key, host: env.VITE_POSTHOG_HOST?.trim() || DEFAULT_HOST};
}

let didInit = false;
let providerSuperProperties: AnalyticsProperties = {};

export type InitAnalyticsArgs = {
  app: string;
  capture?: AnalyticsCapture;
  superProperties?: AnalyticsProperties;
};

/**
 * Idempotent. Safe under React StrictMode. Returns whether PostHog is loaded.
 */
export function initAnalytics({app, capture, superProperties}: InitAnalyticsArgs): boolean {
  const loaded = shouldInit();
  if (!loaded) return false;

  const merged = mergeAnalyticsCapture(capture);
  if (!didInit) {
    const revealText = merged.revealUnmaskClass && supportsHasSelector();
    const initConfig: Partial<PostHogConfig> = {
      api_host: loaded.host,
      person_profiles: merged.personProfiles,
      autocapture: merged.autocapture,
      // `history_change` captures SPA client-side navigations, not just first load.
      capture_pageview: merged.capturePageviews ? 'history_change' : false,
      disable_session_recording: !merged.sessionRecording,
      enable_recording_console_log: false,
      session_recording: {
        maskAllInputs: merged.maskAllInputs,
        ...(revealText && {maskTextSelector: REVEAL_MASK_TEXT_SELECTOR}),
        recordHeaders: false,
        recordBody: merged.recordNetworkBodies,
      },
    };
    posthog.init(loaded.key, initConfig);
    didInit = true;
  }

  providerSuperProperties = {
    app,
    env: analyticsDeployEnv(),
    ...superProperties,
  };
  posthog.register(providerSuperProperties);
  return posthog.__loaded;
}

export function isAnalyticsLoaded(): boolean {
  return posthog.__loaded;
}

export function track(event: string, properties?: AnalyticsProperties): void {
  if (!posthog.__loaded) return;
  posthog.capture(event, properties);
}

export function identifyUser(distinctId: string, traits?: AnalyticsProperties): void {
  if (!posthog.__loaded) return;
  posthog.identify(distinctId, traits);
}

export function resetAnalytics(): void {
  if (!posthog.__loaded) return;
  posthog.reset();
  posthog.register(providerSuperProperties);
}

export function registerSuperProperties(properties: AnalyticsProperties): void {
  if (!posthog.__loaded) return;
  posthog.register(properties);
}

export function unregisterSuperProperty(key: string): void {
  if (!posthog.__loaded) return;
  posthog.unregister(key);
}
